import React from "react";
import PropTypes from "prop-types";
import { Button, Check, Plus, Select, Tag, XClose } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "./BroadcastEditContainer";

import SegmentPicker from "../../../shared/SegmentPicker";

const segmentShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string,
});

const inclusionOptions = [
  { value: true, label: "In" },
  { value: false, label: "Not In" },
];

const styles = {
  row: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  and: {
    lineHeight: "40px",
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  condition: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: "var(--sl-bg-secondary)",
    border: "1px solid var(--sl-border-secondary)",
    color: "var(--sl-text-primary)",
  },
  or: {
    color: "var(--sl-text-tertiary)",
  },
  picker: {
    minWidth: 260,
    maxWidth: 500,
  },
};

const AddSegmentFilterButton = ({
  segmentFilters,
  setSegmentFilters,
}) => (
  <Button
    icon={Plus}
    onClick={() => {
      setSegmentFilters(
        segmentFilters.concat({
          inclusion: true,
          segments: [],
          set: false
        })
      );
    }}
  >
    Add segment condition
  </Button>
);

const SegmentFilter = ({
  editable,
  index,
  segmentFilter,
  destroySegmentFilter,
}) => (
  <div style={styles.row}>
    {index > 0 ? (
      <span style={styles.and}>AND</span>
    ) : null}
    <div style={styles.condition}>
      <span>{segmentFilter.inclusion ? "In" : "Not In"}</span>
      {segmentFilter.segments.map((segment, i) => (
        <React.Fragment key={`segment-${segment.id}`}>
          {i > 0 ? (
            <span style={styles.or}>or</span>
          ) : null}
          <Tag size="sm">{segment.label}</Tag>
        </React.Fragment>
      ))}
      {editable ? (
        <Button
          variant="tertiary"
          size="xs"
          icon={XClose}
          aria-label="Remove condition"
          onClick={() => destroySegmentFilter(segmentFilter)}
        />
      ) : null}
    </div>
  </div>
);

const EditableSegmentFilter = ({
  team,
  index,
  segmentFilter,
  segmentFilters,
  setSegmentFilters,
  createSegmentFilter,
  updateSegmentFilter,
}) => (
  <div style={styles.row}>
    {index > 0 ? (
      <span style={styles.and}>AND</span>
    ) : null}
    <Select
      aria-label="Inclusion"
      width={120}
      disabled={index === 0}
      value={segmentFilter.inclusion}
      options={inclusionOptions}
      onChange={value => {
        setSegmentFilters(
          segmentFilters.map((filter, i) => {
            if (i === index) {
              return {
                ...filter,
                inclusion: value
              };
            }

            return filter;
          })
        );
      }}
    />
    <div style={styles.picker}>
      <SegmentPicker
        multiple
        team={team}
        placeholder="Segment"
        value={{ segmentIds: segmentFilter.segments.map(segment => segment.id) }}
        selectedLabel={segmentFilter.segments.map(segment => segment.label).join(" or ")}
        selectedOptions={segmentFilter.segments.map(segment => ({ value: segment.id, label: segment.label }))}
        excludeIds={segmentFilters.flatMap((filter, i) => (
          i === index ? [] : filter.segments.map(segment => segment.id)
        ))}
        onChange={({ segmentId, label, selected }) => {
          setSegmentFilters(
            segmentFilters.map((filter, i) => {
              if (i === index) {
                return {
                  ...filter,
                  segments: selected ? (
                    filter.segments.concat({ id: segmentId, label })
                  ) : (
                    filter.segments.filter(segment => String(segment.id) !== String(segmentId))
                  )
                };
              }

              return filter;
            })
          );
        }}
      />
    </div>
    <Button
      variant="secondary-color"
      icon={Check}
      aria-label="Apply condition"
      disabled={segmentFilter.segments.length === 0}
      onClick={() => {
        const updated = segmentFilters.map((filter, i) => {
          if (i === index) {
            return {
              ...filter,
              set: true
            };
          }

          return filter;
        })

        setSegmentFilters(updated);

        if(index === 0) {
          createSegmentFilter(updated);
        } else {
          updateSegmentFilter(updated);
        }
      }}
    />
    <Button
      variant="secondary"
      icon={XClose}
      aria-label="Discard condition"
      onClick={() => {
        setSegmentFilters(
          segmentFilters.filter((filter, i) => (i !== index))
        );
      }}
    />
  </div>
);

EditableSegmentFilter.propTypes = {
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  segmentFilter: PropTypes.shape({
    inclusion: PropTypes.bool,
    segments: PropTypes.arrayOf(segmentShape).isRequired,
  }).isRequired,
  segmentFilters: PropTypes.array.isRequired,
  setSegmentFilters: PropTypes.func.isRequired,
  createSegmentFilter: PropTypes.func.isRequired,
  updateSegmentFilter: PropTypes.func.isRequired,
};

const SegmentFilters = ({ editable, showAddButton }) => {
  const {
    team,
    segmentFilters,
    setSegmentFilters,
    visibleSegmentFilters,
    createSegmentFilter,
    updateSegmentFilter,
    destroySegmentFilter,
  } = useBroadcastEditContext();

  return(
    <div>
      {showAddButton ? (
        <AddSegmentFilterButton
          segmentFilters={segmentFilters}
          setSegmentFilters={setSegmentFilters}
        />
      ) : null}
      {visibleSegmentFilters.map((segmentFilter, index) => (
        segmentFilter.set ? (
          <SegmentFilter
            editable={editable}
            key={`segment-filter-${index}`}
            index={index}
            segmentFilter={segmentFilter}
            destroySegmentFilter={destroySegmentFilter}
          />
        ) : (
          <EditableSegmentFilter
            key={`segment-filter-${index}`}
            team={team}
            index={index}
            segmentFilter={segmentFilter}
            segmentFilters={segmentFilters}
            setSegmentFilters={setSegmentFilters}
            createSegmentFilter={createSegmentFilter}
            updateSegmentFilter={updateSegmentFilter}
          />
        )
      ))}
      {editable && visibleSegmentFilters.length !== 0 && segmentFilters.every(filter => filter.set) ? (
        <div style={styles.row}>
          <span style={styles.and}>AND</span>
          <AddSegmentFilterButton
            segmentFilters={segmentFilters}
            setSegmentFilters={setSegmentFilters}
          />
        </div>
      ) : null}
    </div>
  );
}

SegmentFilters.propTypes = {
  editable: PropTypes.bool,
  showAddButton: PropTypes.bool,
};

export default SegmentFilters;
